import { errHof, httpDelete, httpGet, httpPost, httpPut } from "@/http"
import { createAsyncThunk } from "@reduxjs/toolkit"

/** @typedef {import("./types").JobPost} JobPost */
/** @typedef {import("@/types").SuggestedSearch} SuggestedSearch */
/** @typedef {import("./types").FetchJobPostsResult} FetchJobPostResult */
/** @typedef {import("./types").EditJobPostResult} EditJobPostResult */

export const fetchAllJobPostsThunk = createAsyncThunk(
  "jobPosts/user/fetchAllJobPosts",
  async ({ onSuccess, onError }, { rejectWithValue, signal }) => {
    try {
      const data = await httpGet(
        "jobDirectory",
        {},
        {
          signal,
          success: onSuccess,
          error: errHof(onError, "fetchAllJobPostsThunk", rejectWithValue),
        },
      )

      /** @type {JobPost[]} */
      const posts = data.jobPosts

      return posts
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchJobPostThunk = createAsyncThunk(
  "jobPosts/user/fetchJobPost",
  async ({ jobId }, { rejectWithValue, signal }) => {
    try {
      const data = await httpGet(`jobPost/${jobId}`, {}, { signal })

      /** @type {FetchJobPostResult} */
      const ret = {
        selectedJobPost: data.selectedJobPost,
        postedByConstituent: data.postedByConstituent,
        suggestedSearch: data.suggestedSearch,
      }

      return ret
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const submitJobPostThunk = createAsyncThunk(
  "jobPosts/user/submitJobPost",
  async ({ onSuccess, onError }, { rejectWithValue, signal, getState }) => {
    const state = getState().jobPosts.user

    const jobTitle = state.jobTitle
    const jobCompany = state.jobCompany
    const jobDescription = state.jobDescription
    const jobApplicationLink = state.jobApplicationLink
    const jobExpirationDate = state.jobExpirationDate
    const jobLocationCity = state.jobLocationDict.city
    const jobLocationStateCode = state.jobLocationDict.stateCode
    const jobLocationCountryCode = state.jobLocationDict.countryCode
    const jobIsRemote = state.jobIsRemote

    const requestData = {
      jobTitle,
      jobCompany,
      jobDescription,
      jobApplicationLink,
      jobExpirationDate,
      jobLocationCity,
      jobLocationStateCode,
      jobLocationCountryCode,
      jobIsRemote,
    }

    try {
      const resp = await httpPost("jobPost", requestData, {
        signal,
        success: onSuccess,
        error: errHof(onError, "submitJobPostThunk", rejectWithValue),
      })

      /** @type {JobPost[]} */
      const posts = resp.jobPosts

      return posts
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const editJobPostThunk = createAsyncThunk(
  "jobPosts/user/editJobPost",
  async ({ onSuccess, onError }, { rejectWithValue, signal, getState }) => {
    const state = getState().jobPosts.user

    const jobTitle = state.jobTitle
    const jobCompany = state.jobCompany
    const jobDescription = state.jobDescription
    const jobApplicationLink = state.jobApplicationLink
    const jobExpirationDate = state.jobExpirationDate
    const jobLocationCity = state.jobLocationDict.city
    const jobLocationStateCode = state.jobLocationDict.stateCode
    const jobLocationCountryCode = state.jobLocationDict.countryCode
    const jobIsRemote = state.jobIsRemote
    const jobId = state.jobId

    const requestData = {
      jobTitle,
      jobCompany,
      jobDescription,
      jobApplicationLink,
      jobExpirationDate,
      jobLocationCity,
      jobLocationStateCode,
      jobLocationCountryCode,
      jobIsRemote,
    }

    try {
      const resp = await httpPut(`jobPost/${jobId}`, requestData, {
        signal,
        success: onSuccess,
        error: errHof(onError, "editJobPostThunk", rejectWithValue),
      })

      /** @type {EditJobPostResult} */
      const data = {
        jobPosts: resp.jobPosts,
        selectedJobPost: resp.selectedJobPost,
        postedByConstituent: resp.postedByConstituent,
        suggestedSearch: resp.suggestedSearch,
      }

      return data
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const deleteJobPostThunk = createAsyncThunk(
  "jobPosts/user/deleteJobPost",
  async ({ onSuccess, onError }, { rejectWithValue, signal, getState }) => {
    const jobId = getState().jobPosts.user.jobId

    try {
      const resp = await httpDelete(
        `jobPost/${jobId}`,
        {},
        {
          signal,
          success: onSuccess,
          error: errHof(onError, "deleteJobPostThunk", rejectWithValue),
        },
      )

      /** @type {EditJobPostResult} */
      const data = {
        jobPosts: resp.jobPosts,
        selectedJobPost: resp.selectedJobPost,
        postedByConstituent: resp.postedByConstituent,
        suggestedSearch: resp.suggestedSearch,
      }

      return data
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)
